import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { SortOrder } from '@src/store/constants'

import {
  selectDisplayScreens,
  setDisplayScreens,
} from '@store/actionSlices/multiScreen'
import {
  selectProjectIdentity,
  setProjectIdentity,
} from '@store/actionSlices/projectIdentity'
import { selectShowcaseRoomConfig } from '@store/actionSlices/showcase-room'
import { selectToken } from '@store/actionSlices/token'
import {
  DisplayScreen,
  MasterSessionKey,
  SessionListContent,
  Status,
} from '@store/types'

import {
  BaseMasterKey,
  MasterKeyData,
  selectFromResult,
  useGetMasterKeyQuery,
} from '@api/master-key'
import { useLazyGetProjectListQuery } from '@api/project-list'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { hasToken } from '@adUtilities/token-helper'

const POLLING_INTERVAL =
  Number(process.env.REACT_APP_DATA_POLLING_INTERVAL_X_SECONDS || 10) * 1000

const MAX_DISPLAY_SCREENS = 5

const useMasterKeyHandler = () => {
  const dispatch = useDispatch()

  const projectIdentity = useSelector(selectProjectIdentity)
  const showcaseRoomConfig = useSelector(selectShowcaseRoomConfig)
  const token = useSelector(selectToken)
  const displayScreens = useSelector(selectDisplayScreens)

  const { masterKey, sessionId } = projectIdentity
  const { _id: userId } = token

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const skipMasterKeyQuery = useMemo(() => {
    const hasShowcaseRoomConfig = !!Object.keys(showcaseRoomConfig || {}).length

    if (!(hasToken(token) && masterKey && sessionId && hasShowcaseRoomConfig)) {
      return true
    }
    return false
  }, [token, masterKey, sessionId, showcaseRoomConfig])

  const masterKeyQueryResult = useGetMasterKeyQuery(
    {
      masterKey: projectIdentity.masterKey,
      userId,
      sort_order: SortOrder.ASC,
      sort_field: 'created_at',
    },
    {
      selectFromResult,
      skip: skipMasterKeyQuery,
      pollingInterval: POLLING_INTERVAL,
    }
  )

  const [getProjectListTrigger] = useLazyGetProjectListQuery()

  const getSessionName = (sessionKey: string): string =>
    projectIdentity.sessionList.find((item) => item.sessionId === sessionKey)
      ?.sessionName ?? ''

  const processSession = async ({
    projectId,
    key,
  }: MasterSessionKey): Promise<SessionListContent | null> => {
    try {
      const response = await getProjectListTrigger({
        page: 1,
        query: projectId.replace(/[^a-zA-Z0-9-]/g, ' '),
      }).unwrap()

      const project = response?.data?.data?.[0]

      if (!project) {
        return null
      }

      await firebaseControlQuery.updateConnection({
        projectId,
        connected: true,
        sessionId: key,
      })
      return {
        projectId,
        projectName: project.name,
        projectUUID: project.uuid,
        sessionId: key,
        sessionName: getSessionName(key),
      }
    } catch (error) {
      console.error(`Failed to process session ${key}:`, error)
      return null
    }
  }

  const filterDisconnectedSessions = useCallback(
    (sessionKeys: MasterKeyData['sessionKeys']) =>
      sessionKeys.filter(
        (sess) =>
          !projectIdentity?.disconnectedSessions?.some(
            (disc) =>
              disc.projectId === sess.projectId && disc.sessionId === sess.key
          )
      ),
    [projectIdentity?.disconnectedSessions]
  )

  const extractChildSessionKeys = (children: MasterKeyData['children']) => {
    if (!Array.isArray(children) || !children.length) {
      return []
    }

    return children.flatMap((child) => {
      if (child && Array.isArray(child.sessionKeys)) {
        return child.sessionKeys
      }
      return []
    })
  }

  const setupDisplayScreens = useCallback(
    (childMasterKeys: BaseMasterKey[]) => {
      if (!Array.isArray(childMasterKeys) || !childMasterKeys.length) {
        return
      }

      if (!Array.isArray(displayScreens)) {
        return
      }

      if (displayScreens.length === 0) {
        const displayScreensData = childMasterKeys
          .slice(0, MAX_DISPLAY_SCREENS)
          .map((item) => ({
            label: item.label || item.masterKey || '',
            currentProject: projectIdentity.projectName,
            masterKey: item.masterKey,
            sessionKeys: item.sessionKeys,
          }))

        dispatch(setDisplayScreens(displayScreensData))
        return
      }

      const currentScreensByMasterKey = new Map(
        displayScreens.map((screen) => [screen.masterKey, screen])
      )

      const existingItems: BaseMasterKey[] = []
      const newItems: BaseMasterKey[] = []

      childMasterKeys.forEach((item) =>
        currentScreensByMasterKey.has(item.masterKey)
          ? existingItems.push(item)
          : newItems.push(item)
      )

      const updatedExistingScreens: DisplayScreen[] = existingItems.map(
        (item) => {
          const currentScreen = currentScreensByMasterKey.get(item.masterKey)
          return {
            label: item.label || item.masterKey || '',
            currentProject:
              currentScreen?.currentProject ?? projectIdentity.projectName,
            masterKey: item.masterKey,
            sessionKeys: item.sessionKeys,
          }
        }
      )

      const availableSlots = MAX_DISPLAY_SCREENS - updatedExistingScreens.length

      let newScreensToAdd: DisplayScreen[] = []

      if (newItems.length > 0 && availableSlots > 0) {
        newScreensToAdd = newItems
          .slice(0, Math.max(0, availableSlots))
          .map((item) => ({
            label: item.label || item.masterKey || '',
            currentProject: projectIdentity.projectName,
            masterKey: item.masterKey,
            sessionKeys: item.sessionKeys,
          }))
      }

      const finalScreens = [...updatedExistingScreens, ...newScreensToAdd]
      dispatch(setDisplayScreens(finalScreens))
    },
    [displayScreens, projectIdentity.projectName, dispatch]
  )

  const handleMasterSessionKeys = useCallback(
    async (materKeyData: MasterKeyData) => {
      const { sessionKeys, isParent, children } = materKeyData

      if (!Array.isArray(sessionKeys) || !sessionKeys.length) {
        return
      }

      const hasChildren =
        isParent && Array.isArray(children) && children.length > 0

      if (hasChildren) {
        setupDisplayScreens(children)
      }

      const parentActiveSessions = filterDisconnectedSessions(sessionKeys)

      const parentSessionResults = await Promise.all(
        parentActiveSessions.map(processSession)
      )

      if (hasChildren) {
        const childSessionKeys = extractChildSessionKeys(children)
        const childActiveSessions = filterDisconnectedSessions(childSessionKeys)

        await Promise.all(childActiveSessions.map(processSession))
      }

      const sessionList = parentSessionResults.filter(
        (result): result is SessionListContent => result != null
      )

      dispatch(
        setProjectIdentity({
          ...projectIdentity,
          ...showcaseRoomConfig,
          sessionList,
        })
      )
    },
    [masterKeyQueryResult, showcaseRoomConfig]
  )

  useEffect(() => {
    const { masterKeyData, isError, status } = masterKeyQueryResult

    if (
      isError ||
      status !== Status.FULFILLED ||
      !Object.keys(masterKeyData).length
    ) {
      return
    }

    handleMasterSessionKeys(masterKeyData)
  }, [handleMasterSessionKeys])
}

export default useMasterKeyHandler
